import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import Filter from '../components/Filter';

import { useForm } from 'react-hook-form';
import VideoFunctionDetailModal from '../components/VideoFunctionDetailModal';
import VideoFunctionListTable from '../components/VideoFunctionListTable';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useHistory } from 'react-router-dom';
import { VideoFunction, VideoFunctionListItem, VideoFunctionListRequestPayload } from '../models/videoFunction';
import videoFunctionListService from '../videoFunctionServiceList';
import { selectVideoFunctionData, videoFunctionActions } from '../videoFunctionSlice';

type VideoFunctionListFormValues = {
  videoFunction: VideoFunctionListItem[];
  forceSearch: boolean;
};

function VideoFunctionPage() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const dispatch = useAppDispatch();

  //ref
  const btnAddVideoRef = useRef<HTMLButtonElement>(null);
  const btnEditVideoRef = useRef<HTMLButtonElement>(null);
  const btnDeleteVideoRef = useRef<HTMLButtonElement>(null);
  const btnSaveVideoRef = useRef<HTMLButtonElement>(null);
  const formSearchRef = useRef<HTMLFormElement>(null);
  const priorityChangedRef = useRef(false);
  const nextLocationRef = useRef<Location | null>(null);

  //state
  const [showVideoFunctionDetailModal, setShowVideoFunctionDetailModal] = useState(false);
  const [selectedVideoFunctionId, setSelectedVideoFunctionId] = useState<number>();
  const [videoChangedDataList, setVideoChangedDataList] = useState<VideoFunction[]>([]);
  const [isTryingToLeave, setIsTryingToLeave] = useState(false);

  //history
  const history = useHistory();

  // selector
  const videoFunctionList = useAppSelector(selectVideoFunctionData);
  const isEditMode = useAppSelector(selectIsEditMode);

  //Store List - Hook Form
  // eslint-disable-next-line
  const { register, control, handleSubmit, reset } = useForm<VideoFunctionListFormValues>({
    defaultValues: {
      videoFunction: [],
      forceSearch: true,
    },
    mode: 'onBlur',
  });
  const hasUnsavedChanges = () => {
    return videoFunctionList.some((item) => item.temp_delete_flg !== item.delete_flg) || priorityChangedRef.current;
  };

  useEffect(() => {
    const unblock = history.block((location) => {
      if (!isTryingToLeave && hasUnsavedChanges()) {
        nextLocationRef.current = location as unknown as Location;
        setIsTryingToLeave(true);
        return false;
      }
      return;
    });

    return () => {
      unblock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges]);

  // handle status button actions
  useEffect(() => {
    if (btnAddVideoRef.current) btnAddVideoRef.current.disabled = false;
    if (btnEditVideoRef.current) btnEditVideoRef.current.disabled = true;
    if (btnDeleteVideoRef.current) btnDeleteVideoRef.current.disabled = true;
    if (btnSaveVideoRef.current) btnSaveVideoRef.current.disabled = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedRowChanged = useCallback(function () {
    if (btnAddVideoRef.current && btnEditVideoRef.current && btnDeleteVideoRef.current && btnSaveVideoRef.current) {
      const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-videofunction-item > input:checked');

      if (chkSelectionElems.length === 0) {
        btnEditVideoRef.current.disabled = true;
        btnDeleteVideoRef.current.disabled = true;
      } else if (chkSelectionElems.length === 1) {
        const chkSelectionElem = chkSelectionElems[0];
        if (chkSelectionElem.dataset.type === 'videofunction') {
          btnEditVideoRef.current.disabled = false;
          btnDeleteVideoRef.current.disabled = false;
        }
      } else {
        btnEditVideoRef.current.disabled = true;
        btnDeleteVideoRef.current.disabled = false;
      }
    }
  }, []);

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-videofunction-item > input');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = false;
    });
    if (btnEditVideoRef.current) btnEditVideoRef.current.disabled = true;
    if (btnDeleteVideoRef.current) btnDeleteVideoRef.current.disabled = true;
  }

  // API
  function loadVideoFunctionListData(dataChanged?: VideoFunctionListItem) {
    try {
      if (formSearchRef.current) {
        // reset selected row
        dispatch(videoFunctionActions.unselectAll());

        const showFlg = Number(formSearchRef.current['show_flg'].value.trim());

        (async () => {
          try {
            const params: VideoFunctionListRequestPayload = {
              display_type: 1,
              show_flg: showFlg,
            };
            const response = await videoFunctionListService.getVideoFunctionList(params);
            const initializedData = response.data.map((item: VideoFunctionListItem) => ({
              ...item,
              temp_delete_flg: item.delete_flg,
            }));

            if (dataChanged) {
              const updatedVideoFunctionList = videoFunctionList.map((item) =>
                item.id === dataChanged!.id ? { ...item, ...dataChanged } : item
              );
              dispatch(videoFunctionActions.setVideoFunctionList(updatedVideoFunctionList));
            } else {
              // setVideoFunctionList(initializedData);
              dispatch(videoFunctionActions.setVideoFunctionList(initializedData));
            }
          } catch (e) {
            if (!handleServiceError(showGlobalModal, e as AxiosError)) {
              displayError(showGlobalModal, e as AxiosError);
            }
          }
        })();
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  useEffect(() => {
    loadVideoFunctionListData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSearchSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (is_delete_restore_modified() || is_data_changed() || priorityChangedRef.current) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('videoFunction.warning_save_data')}
          onConfirm={handleSubmit(onSave)}
        />
      ));
    } else {
      handleUnSelectAll();
      loadVideoFunctionListData();
    }
  }

  function handlePriorityChanged(hasChanged: boolean) {
    if (hasChanged) {
      priorityChangedRef.current = true;
    }
  }

  function is_delete_restore_modified() {
    const modifiedData = videoFunctionList.filter((item) => {
      return item.temp_delete_flg !== item.delete_flg;
    });
    if (modifiedData.length > 0) {
      return true;
    }
    return false;
  }

  function is_data_changed() {
    const count = videoChangedDataList.length;
    if (count > 0) {
      return true;
    }
    return false;
  }

  function handleCloseVideoFunctionDetailModal(isDataChanged: boolean, dataChanged?: any) {
    setShowVideoFunctionDetailModal(false);
    setSelectedVideoFunctionId(undefined);

    if (isDataChanged && dataChanged) {
      btnSaveVideoRef.current!.disabled = false;
      setVideoChangedDataList((prevList: VideoFunction[]) => {
        const exists = prevList.some((item) => item.id === dataChanged.id);
        if (exists) {
          // replace the item
          return prevList.map((item) => (item.id === dataChanged.id ? { ...item, ...dataChanged } : item));
        } else {
          // append item
          return [...prevList, dataChanged];
        }
      });
      loadVideoFunctionListData(dataChanged);
    }
  }

  // Add modal function
  function handleAddVideoFunctionModal() {
    setShowVideoFunctionDetailModal(true);
  }

  // Edit modal function
  function handleOpenEditVideoFunctionModal() {
    const chkSelectionElems = document.querySelectorAll('.chk-videofunction-item > input:checked');
    if (chkSelectionElems.length > 0) {
      if (chkSelectionElems.length > 1) {
        return;
      }

      const selectedElementChk = chkSelectionElems[0] as HTMLInputElement;

      if (selectedElementChk.dataset.type === 'videofunction') {
        const videoFunctionId = Number(selectedElementChk.value);
        setSelectedVideoFunctionId(videoFunctionId);
        setShowVideoFunctionDetailModal(true);
      }
    }
  }

  // Delete modal function
  async function processDelete() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-videofunction-item > input:checked');
    if (chkSelectionElems.length > 0) {
      //compose delete data
      const payload = { ids: [] as number[] };

      chkSelectionElems.forEach((element) => {
        if (element.dataset.type === 'videofunction') {
          const videoFunctionId = Number(element.value);
          payload.ids.push(videoFunctionId);
        }
      });

      const newVideoFunctionList = videoFunctionList.map((item) =>
        payload.ids.includes(item.id) ? { ...item, temp_delete_flg: true } : item
      );
      dispatch(videoFunctionActions.setVideoFunctionList(newVideoFunctionList));
    }
  }

  async function handleConfirmDelete() {
    await processDelete();
    showGlobalModal(() => <AlertModal title={t('App.info')} message={t('videoFunction.success_delete')} />);
    handleUnSelectAll();
  }

  function handleDeleteVideoFunctionModal() {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('videoFunction.warn_confirm_delete')}
        onConfirm={handleConfirmDelete}
      />
    ));
  }

  // Save function
  const onSave = async (data: VideoFunctionListFormValues) => {
    const modifiedData = videoFunctionList.filter((item) => item.temp_delete_flg !== item.delete_flg);
    let changeData = videoChangedDataList;

    try {
      if (modifiedData.length > 0) {
        const deleteIds: number[] = [];
        const restoreIds: number[] = [];

        modifiedData.forEach((item) => {
          if (item.temp_delete_flg) {
            deleteIds.push(item.id);
          } else {
            restoreIds.push(item.id);
          }
        });
        const allIds = [...deleteIds, ...restoreIds]; // Collect all relevant IDs
        const modifiedDataMap = new Map(modifiedData.map((item) => [item.id, item]));

        if (changeData && changeData.length === 0) {
          // Handle delete and restore if not change information
          changeData = modifiedData.map((item) => {
            const updatedItem: VideoFunction = {
              id: item.id,
              name: item.name,
              priority: item.priority,
              display_name_ja: null,
              display_name_en: null,
              display_name_vi: null,
              display_name_ko: null,
              display_name_zh_hant: null,
              display_name_zh_hans: null,
              thumbnail_img: null,
              video: null,
              note: item.note,
              delete_flg: restoreIds.includes(item.id) ? false : deleteIds.includes(item.id) ? true : item.delete_flg,
            };
            return updatedItem;
          });
        } else {
          // Handle delete and restore if change information before
          changeData = allIds
            .map((id) => {
              const existingItem = changeData.find((item) => item.id === id);
              const modifiedItem = modifiedDataMap.get(id);

              if (existingItem) {
                // Update existing item
                return {
                  ...existingItem,
                  delete_flg: restoreIds.includes(id) ? false : deleteIds.includes(id) ? true : existingItem.delete_flg,
                };
              } else if (modifiedItem) {
                // Add new item from modifiedData
                return {
                  id: modifiedItem.id,
                  name: modifiedItem.name,
                  priority: modifiedItem.priority,
                  display_name_ja: null,
                  display_name_en: null,
                  display_name_vi: null,
                  display_name_ko: null,
                  display_name_zh_hant: null,
                  display_name_zh_hans: null,
                  thumbnail_img: null,
                  video: null,
                  note: modifiedItem.note,
                  delete_flg: restoreIds.includes(modifiedItem.id)
                    ? false
                    : deleteIds.includes(modifiedItem.id)
                    ? true
                    : modifiedItem.delete_flg,
                };
              }
              return null;
            })
            .filter((item): item is VideoFunction => item !== null); // remove null items
        }
      }

      // Handle updates
      let updateDataPayload: VideoFunction[] = videoFunctionList.map((item, index) => {
        const updatedItem: VideoFunction = {
          id: item.id,
          name: item.name,
          priority: index + 1,
          display_name_ja: null,
          display_name_en: null,
          display_name_vi: null,
          display_name_ko: null,
          display_name_zh_hant: null,
          display_name_zh_hans: null,
          thumbnail_img: null,
          video: null,
          note: item.note,
          delete_flg: item.delete_flg,
        };
        return updatedItem;
      });

      if (changeData.length > 0 || modifiedData.length > 0) {
        updateDataPayload = updateDataPayload.map((item) => {
          const foundChangeData = changeData.find((changeDataItem) => changeDataItem.id === item.id);
          if (foundChangeData) return { ...foundChangeData, priority: item.priority };

          return item;
        });
      }
      await videoFunctionListService.updates(updateDataPayload);

      // Show success modal
      showGlobalModal(() => (
        <AlertModal
          title={t('App.info')}
          message={t('videoFunction.sucesss_save')}
          onClose={() => {
            data.forceSearch = false;
          }}
        />
      ));

      // Update video function list state
      const updatedVideoFunctionList = videoFunctionList.map((item) => {
        const modifiedItem = modifiedData.find((modItem) => modItem.id === item.id);
        if (modifiedItem) {
          return {
            ...item,
            delete_flg: modifiedItem.temp_delete_flg,
            temp_delete_flg: modifiedItem.temp_delete_flg,
          };
        }
        return item;
      });
      loadVideoFunctionListData();
      dispatch(videoFunctionActions.setVideoFunctionList(updatedVideoFunctionList));
      setVideoChangedDataList([]);
      priorityChangedRef.current = false;
    } catch (error) {
      if (!handleServiceError(showGlobalModal, error as AxiosError)) {
        displayError(showGlobalModal, error as AxiosError);
      }
    }
  };

  // handle restore video function item
  const handleRestoreVideoFunction = (videoFunctionId: number) => {
    try {
      const newVideoFunctionList = videoFunctionList.map((item) =>
        item.id === videoFunctionId ? { ...item, temp_delete_flg: false } : item
      );
      dispatch(videoFunctionActions.setVideoFunctionList(newVideoFunctionList));
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('videoFunction.success_restore')} />);
      handleUnSelectAll();
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  };
  useEffect(() => {
    if (isTryingToLeave && nextLocationRef.current) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('videoFunction.warning_save_data')}
          onConfirm={() => {
            handleSubmit(onSave)();
            setIsTryingToLeave(false);
          }}
          onCancel={() => {
            const unblock = history.block(() => {});
            unblock();
            history.push(nextLocationRef.current!.pathname);
          }}
        />
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTryingToLeave]);

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <FeatureTitleBar title={t('videoFunctionDetailModal.title')} />
          </div>
          <Filter onSearchSubmit={handleSearchSubmit} formSearchRef={formSearchRef} />
          <div className="label">{t('videoFunction.hint')}</div>
          <div className="label ms-3">{t('videoFunction.hint_1')}</div>
          <div className="text-end mb-2">
            <Button ref={btnAddVideoRef} className="btn--dark-green ms-3" onClick={handleAddVideoFunctionModal}>
              {t('videoFunction.button.add')}
            </Button>
            <Button ref={btnEditVideoRef} className="ms-3" onClick={handleOpenEditVideoFunctionModal}>
              {t('videoFunction.button.edit')}
            </Button>
            <Button
              ref={btnDeleteVideoRef}
              className="btn--red ms-3 m-5 mt-0 mb-0"
              onClick={handleDeleteVideoFunctionModal}
            >
              {t('videoFunction.button.delete')}
            </Button>

            <Button ref={btnSaveVideoRef} className="btn--dark-green ms-3" onClick={handleSubmit(onSave)}>
              {t('videoFunction.button.save')}
            </Button>
          </div>
          <VideoFunctionListTable
            videoFunctionList={videoFunctionList}
            onRestoreVideoFunction={handleRestoreVideoFunction}
            onSelectedRowChanged={handleSelectedRowChanged}
            onPriorityChange={handlePriorityChanged}
            onOpenEditVideoFunctionModal={function (): void {
              throw new Error('Function not implemented.');
            }}
          />
          {/* Add and Edit modal */}
          {showVideoFunctionDetailModal &&
            createPortal(
              <VideoFunctionDetailModal
                videoFunctionId={selectedVideoFunctionId}
                videoChangedData={videoChangedDataList}
                isShowModal={true}
                onClose={handleCloseVideoFunctionDetailModal}
                onUnSelectAll={handleUnSelectAll}
                onReloadData={loadVideoFunctionListData}
              />,
              document.body
            )}
        </div>
      </div>
    </div>
  );
}

export default VideoFunctionPage;
